import type { RequestTogglePauseResponse } from "@nlc/shared-types";

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { toast } from "@/components/toast/toast-queue.ts";
import { useEditRequestFormContext } from "@/features/request/edit/hooks/useEditRequestForm";
import {
  invalidateStatusChangeQueries,
  scheduleQueueAutomationRefetch,
} from "@/features/requests/utils/requestInvalidation";
import {
  assignableDesignersKeys,
  invalidateQueryKeys,
  requestKeys,
} from "@/hooks/queryKeys";
import { useGetRequest } from "@/hooks/useGetRequest.tsx";
import apiClient from "@/lib/api/client";

export function useTogglePause() {
  const queryClient = useQueryClient();
  const { setValue } = useEditRequestFormContext();
  const { data: request } = useGetRequest();
  const requestId = request?.id;
  const assignationKeys = requestId
    ? [
        requestKeys.requestById(requestId),
        assignableDesignersKeys.assignableDesignersByRequestId(requestId),
      ]
    : [];

  const { isPending, mutate: togglePause } = useMutation({
    mutationFn: async () =>
      apiClient
        .url(`/requests/${requestId}/toggle-pause`)
        .post({})
        .json<RequestTogglePauseResponse>(),
  });

  const pauseRequest = () => {
    togglePause(undefined, {
      onError: () => {
        toast.add({
          description: "Please try again later",
          timeout: 2000,
          title: "Error pausing/continuing request",
          type: "error",
        });
      },
      onSuccess: (response) => {
        const action = response.message.includes("paused")
          ? "paused"
          : "continued";

        toast.add({
          description: `Request has been ${action} successfully.`,
          timeout: 2000,
          title: `Request ${action}`,
          type: "success",
        });

        setValue("statusId", response.newStatusId);

        invalidateQueryKeys(queryClient, assignationKeys);
        invalidateStatusChangeQueries(queryClient);
        scheduleQueueAutomationRefetch(queryClient, assignationKeys);
      },
    });
  };

  return {
    isPending,
    mutate: togglePause,
    pauseRequest,
  };
}
